import Phaser from "phaser";
import { NoteController } from "../controller/NoteController";
import { NoteRenderer } from "../view/NoteRenderer";
import { getRuntimeSession } from "../core/RuntimeSession";

export interface NoteSceneInitData {
  readonly notes: Readonly<Record<string, string | null>>;
}

export class NoteScene extends Phaser.Scene {
  public static readonly KEY = "NoteScene";
  private controller!: NoteController;
  private noteRenderer!: NoteRenderer;

  constructor() {
    super(NoteScene.KEY);
  }

  init(data: NoteSceneInitData): void {
    this.controller = new NoteController(data.notes);
  }

  create(): void {
    this.noteRenderer = new NoteRenderer(this);
    this.noteRenderer.render(this.controller.getState());
    this.setupInput();
  }

  private setupInput(): void {
    this.input.keyboard!.on("keydown", (event: KeyboardEvent) => {
      event.preventDefault();

      const state = this.controller.getState();
      const code = event.code;
      const key = event.key;

      if (code === "Escape") {
        this.close();
        return;
      }

      if (code === "Tab") {
        this.close();
        return;
      }

      if (state.isEditing) {
        if (key.length === 1 && /[a-zA-Z0-9]/.test(key)) {
          this.controller.recordChar(key.toUpperCase());
          this.controller.exitEditMode();
        } else if (code === "Space") {
          this.controller.recordChar("_");
          this.controller.exitEditMode();
        } else if (code === "Backspace" || code === "Delete") {
          this.controller.deleteChar();
        } else {
          this.controller.exitEditMode();
        }
      } else {
        if (code === "ArrowUp" || code === "KeyW") this.controller.moveUp();
        else if (code === "ArrowDown" || code === "KeyS") this.controller.moveDown();
        else if (code === "ArrowLeft" || code === "KeyA") this.controller.moveLeft();
        else if (code === "ArrowRight" || code === "KeyD") this.controller.moveRight();
        else if (code === "Backspace" || code === "Delete") this.controller.deleteChar();
        else if (code === "Enter" || code === "KeyE") this.controller.enterEditMode();
      }

      this.noteRenderer.render(this.controller.getState());
      void this.persistNotes();
    });
  }

  private async persistNotes(): Promise<void> {
    const session = getRuntimeSession();
    const currentSave = session.getCurrentSave();
    const updatedSave = {
      ...currentSave,
      symbols: {
        ...currentSave.symbols,
        notes: this.controller.getAllNotes(),
      },
    };
    await session.updateSave(updatedSave);
  }

  private close(): void {
    this.scene.stop(NoteScene.KEY);
  }
}
